/**
 * ActionExecutor - Manages action execution with middleware support
 * 
 * Features:
 * - Middleware chain for cross-cutting concerns
 * - Error handling and recovery
 * - Execution metrics and logging
 * - Action cancellation support
 */

class ActionExecutor {
  constructor() {
    this.actions = new Map(); // Map<string, ActionDefinition>
    this.middlewares = []; // Array of middleware functions
    this.executionHistory = []; // Recent executions for debugging
    this.maxHistorySize = 100;
  }

  /**
   * Register an action
   * @param {string} name - Unique action identifier
   * @param {Function} handler - Action handler function
   * @param {Object} metadata - Optional metadata
   * @returns {string} - Action ID for unregistering
   */
  register(name, handler, metadata = {}) {
    if (!name || typeof handler !== 'function') {
      throw new Error('Action name and handler function are required');
    }

    const actionDef = {
      name,
      handler,
      metadata: {
        description: metadata.description || '',
        category: metadata.category || 'general',
        async: metadata.async !== false,
        ...metadata
      },
      executeCount: 0,
      lastExecuted: null,
      registeredAt: Date.now(),
    };

    this.actions.set(name, actionDef);
    return name;
  }

  /**
   * Unregister an action
   * @param {string} name - Action name to remove
   * @returns {boolean} - True if action was removed
   */
  unregister(name) {
    return this.actions.delete(name);
  }

  /**
   * Execute an action through the middleware chain
   * @param {string} actionName - Action to execute
   * @param {Object} context - Execution context
   * @returns {Promise<boolean>} - True if execution succeeded
   */
  async execute(actionName, context = {}) {
    const actionDef = this.actions.get(actionName);
    if (!actionDef) {
      console.warn(`[ActionExecutor] Unknown action: ${actionName}`);
      this.recordExecution(actionName, false, 'Action not found', context);
      return false;
    }

    // Create execution context
    const execContext = {
      actionName,
      action: actionDef,
      context,
      timestamp: Date.now(),
      cancelled: false,
      error: null,
      result: null,
      metadata: {},
    };

    try {
      // Run through middleware chain
      for (let i = 0; i < this.middlewares.length; i++) {
        const middleware = this.middlewares[i];
        const middlewareResult = await this.runMiddleware(middleware, execContext, i);
        
        if (middlewareResult === false || execContext.cancelled) {
          this.recordExecution(actionName, false, 'Cancelled by middleware', execContext);
          return false;
        }
      }

      // Execute the actual action
      const startTime = performance.now();
      
      if (actionDef.metadata.async) {
        execContext.result = await actionDef.handler(execContext.context);
      } else {
        execContext.result = actionDef.handler(execContext.context);
      }

      const executionTime = performance.now() - startTime;
      
      // Update action statistics
      actionDef.executeCount++;
      actionDef.lastExecuted = execContext.timestamp;
      
      // Record successful execution
      this.recordExecution(actionName, true, null, execContext, executionTime);
      
      return true;

    } catch (error) {
      console.error(`[ActionExecutor] Action "${actionName}" failed:`, error);
      execContext.error = error;
      this.recordExecution(actionName, false, error.message, execContext);
      return false;
    }
  }

  /**
   * Run a single middleware
   * @param {Function} middleware - Middleware function
   * @param {Object} execContext - Execution context
   * @param {number} index - Middleware index for debugging
   */
  async runMiddleware(middleware, execContext, index) {
    try {
      const result = await middleware(execContext);
      return result;
    } catch (error) {
      console.error(`[ActionExecutor] Middleware ${index} failed:`, error);
      execContext.cancelled = true;
      execContext.error = error;
      return false;
    }
  }

  /**
   * Add middleware to the execution chain
   * @param {Function} middleware - Middleware function
   * @returns {Function} - Cleanup function to remove middleware
   */
  use(middleware) {
    if (typeof middleware !== 'function') {
      throw new Error('Middleware must be a function');
    }

    this.middlewares.push(middleware);
    
    // Return cleanup function
    return () => {
      const index = this.middlewares.indexOf(middleware);
      if (index > -1) {
        this.middlewares.splice(index, 1);
      }
    };
  }

  /**
   * Record execution for history/debugging
   */
  recordExecution(actionName, success, error, execContext, executionTime = 0) {
    const record = {
      actionName,
      success,
      error,
      timestamp: Date.now(),
      executionTime,
      context: execContext?.context || {},
      metadata: execContext?.metadata || {},
    };

    this.executionHistory.push(record);
    
    // Keep history size manageable
    if (this.executionHistory.length > this.maxHistorySize) {
      this.executionHistory.shift();
    }
  }

  /**
   * Get action statistics
   * @param {string} actionName - Optional, get stats for specific action
   * @returns {Object} - Statistics object
   */
  getStats(actionName = null) {
    if (actionName) {
      const action = this.actions.get(actionName);
      if (!action) return null;
      
      const recentExecutions = this.executionHistory
        .filter(e => e.actionName === actionName)
        .slice(-10);
      
      return {
        name: actionName,
        executeCount: action.executeCount,
        lastExecuted: action.lastExecuted,
        registeredAt: action.registeredAt,
        recentExecutions,
        metadata: action.metadata,
      };
    }

    // Global stats
    const totalActions = this.actions.size;
    const totalExecutions = Array.from(this.actions.values())
      .reduce((sum, action) => sum + action.executeCount, 0);
    
    const recentSuccessRate = this.executionHistory.slice(-20)
      .reduce((rate, exec) => rate + (exec.success ? 1 : 0), 0) / 
      Math.min(20, this.executionHistory.length);

    return {
      totalActions,
      totalExecutions,
      totalMiddlewares: this.middlewares.length,
      recentSuccessRate: recentSuccessRate || 0,
      historySize: this.executionHistory.length,
    };
  }

  /**
   * Get all registered actions
   * @returns {Array} - Array of action definitions
   */
  getAllActions() {
    return Array.from(this.actions.values()).map(action => ({
      name: action.name,
      metadata: action.metadata,
      executeCount: action.executeCount,
      lastExecuted: action.lastExecuted,
    }));
  }

  /**
   * Clear execution history
   */
  clearHistory() {
    this.executionHistory = [];
  }

  /**
   * Export execution data for debugging
   */
  export() {
    return {
      actions: Object.fromEntries(this.actions),
      middlewares: this.middlewares.length,
      history: this.executionHistory.slice(-50), // Last 50 executions
      stats: this.getStats(),
      timestamp: Date.now(),
    };
  }
}

// Common middleware functions

/**
 * Logging middleware - logs all action executions
 */
export const loggingMiddleware = (execContext) => {
  if (import.meta.env.DEV) {
    console.log(`[Keybinding] Executing: ${execContext.actionName}`, execContext.context);
  }
  return true;
};

/**
 * Rate limiting middleware - prevents rapid repeated executions
 */
export const rateLimitMiddleware = (cooldownMs = 100) => {
  const lastExecution = new Map();

  return (execContext) => {
    const { actionName } = execContext;
    const now = Date.now();
    const last = lastExecution.get(actionName);

    if (last && now - last < cooldownMs) {
      if (import.meta.env.DEV) {
        console.log(`[Keybinding] Rate limited: ${actionName}`);
      }
      return false;
    }

    lastExecution.set(actionName, now);
    return true;
  };
};

/**
 * Performance monitoring middleware
 */
export const performanceMiddleware = (execContext) => {
  execContext.metadata.startTime = performance.now();
  
  // Add cleanup to record end time
  const originalHandler = execContext.action.handler;
  execContext.action.handler = async (...args) => {
    const result = await originalHandler(...args);
    execContext.metadata.endTime = performance.now();
    execContext.metadata.duration = execContext.metadata.endTime - execContext.metadata.startTime;
    return result;
  };
  
  return true;
};

// Create singleton instance
const actionExecutor = new ActionExecutor();

// Register default middleware
actionExecutor.use(loggingMiddleware);
actionExecutor.use(rateLimitMiddleware(100)); // 100ms cooldown

export default actionExecutor;